import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Divider, Paper, Grid, Box, Fab, ButtonGroup, Button, Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useSnackbar } from 'notistack';
import Modal from 'components/Modal';
import AddBooking from './Add';
import AddIcon from '@mui/icons-material/Add';
import PreviewIcon from '@mui/icons-material/Preview';
import { buildTableDataAndColumns, getBookingStatus, tConv24 } from 'utils/common';
import ScrollDialog from 'components/ScrollDialog';
import CancelBooking from './CancelBooking';
import { getLibraryBookingDetails, getAvailableSeats, getBookingsByProperty } from 'services/bookings';
import SeatLayout from 'components/SeatLayout';
import LibrarySelect from 'components/librarySelect';
import { Stack } from '@mui/system';
import ReactDataTableBuilder from 'components/tableBuilder';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ChairIcon from '@mui/icons-material/Chair';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import AutorenewIcon from '@mui/icons-material/Autorenew';

const statusList = [
  {name: 'Ongoing', id: 'ONGOING', color: 'warning'},
  {name: 'Upcoming', id: 'UPCOMING', color: 'info'},
  {name: 'Completed', id: 'COMPLETED', color: 'success'},
  {name: 'Cancelled', id: 'CANCELLED', color: 'error'},
  // {name: 'Payment Due', id: 'PAYMENT_DUE'},
]

const BookingsPage = () => {
  const defaultMoreActionState = {action: null, data: null};

  const [bookingsList, setBookingsList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [availableSeats, setAvailableSeats] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [moreActions, setMoreActions] = useState({...defaultMoreActionState});
  const [selectedLibrary, setSelectedLibrary] = useState({});
  const [filterStatus, setFilterStatus] = useState('ONGOING');
  const [tableData, setTableData] = useState({});


  const onLibrarySelected = useCallback((lib) => {
    setSelectedLibrary({...lib});
  },[]);
  

  const getBookingsData = useCallback(() => {
    const propertyId = selectedLibrary?.libraryId;
    const propertyType = 'LIBRARY';
    const bookingsFilter = filterStatus;
    getBookingsByProperty({propertyId, propertyType, bookingsFilter}).then(({response, error}) => {
      if(response){
        setBookingsList([...(response || [])]);
      } else {
        setBookingsList([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }, [enqueueSnackbar, filterStatus, selectedLibrary?.libraryId])

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
    },
    [setOpenModal],
  )  
 
  const onBookingAdded = useCallback(
    () => {
      setOpenModal(false);
      getBookingsData();
    },
    [setOpenModal, getBookingsData],
  )

  const onAddBookingClick = () => {
    setBookingDetails(null);
    setOpenModal(true);
  }


  function viewBooking(bookingId) {
    getLibraryBookingDetails({bookingId}).then(({response, error}) => {
      if(response){
        setBookingDetails({...response})
        setOpenModal(true);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }
 
  const closeAllModals = () => {
    setMoreActions({...defaultMoreActionState});
  }

  const closeCancelBookingModal = () => {
    closeAllModals();
  }

  const onBookingCancelled = useCallback((e) => {
    closeAllModals();
    getBookingsData();
  }, [getBookingsData]);

  function getSeatAvailability({bookingId, libraryId}) {
    getAvailableSeats({ bookingId, libraryId}).then(({response, error}) => {
      if(response){
        setAvailableSeats(response);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function onSeatAssigned() {
    closeAllModals();
    getBookingsData();
  }

  function statusChanged(status) {
    setFilterStatus(status)
  }

  useEffect(() => {
    if(filterStatus && selectedLibrary?.libraryId){
      getBookingsData()
    }
  }, [filterStatus, selectedLibrary]);

  const onActionBtnClick = useCallback((e, action, data) => {
    e.preventDefault();
    if(action === 'assignSeat' || action === 'changeSeat') {
      getSeatAvailability(data);
      setMoreActions({action, data})
    }else if(action === 'renewBooking') {
      const { user, library,  } = data;
      const libData = {
        user, library, libraryId: library?.libraryId
      };
      setBookingDetails({...libData});
      setMoreActions({action, data})
      setOpenModal(true);
    }else if(action === 'viewBooking') {
      viewBooking(data?.bookingId);
    }else{
      setMoreActions({action, data})
    }
  }, []);

  const actionRender = useCallback(({data}) => {
    const status = getBookingStatus(data);
    return (
      <Stack direction="row" spacing={1}>
        <Box>
          <Tooltip title="View Booking" arrow placement="left">
            <IconButton onClick={(e) => onActionBtnClick(e, 'viewBooking', data)}>
              <PreviewIcon sx={{color: '#916fac'}} />
            </IconButton>
          </Tooltip>
        </Box>
        { (status === 'Upcoming' || status === 'Ongoing') && data?.seatCount > 0 ? (
          <Box>
            <Tooltip title="Change Seat" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'changeSeat', data)}>
                <AirlineSeatReclineNormalIcon color="success" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null} 
        { (status === 'Upcoming' || status === 'Ongoing') && data?.seatCount === 0 ? (
          <>
            <Box>
              <Tooltip title="Assign Seat" arrow placement="left">
                <IconButton onClick={(e) => onActionBtnClick(e, 'assignSeat', data)}>
                  <ChairIcon color="info" />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        ) : null} 
        { status === 'Upcoming' || status === 'Ongoing'? (
          <Box>
            <Tooltip title="Cancel Booking" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'cancelBooking', data)}>
                <EventBusyIcon color="error" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
        { status === 'Completed' || status === 'Ongoing'? (
          <Box>
            <Tooltip title="Renew Booking" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'renewBooking', data)}>
                <AutorenewIcon color="warning" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null}
      </Stack>
    )
  },[onActionBtnClick]);

  const buildTableColumns = useCallback(() => {
    const columns=[
      {name: "Name", key: "user.displayName", dataKey: 'displayName', columnOptions: {defaultWidth: 180}},
      {name: "Mobile No", key: "user.mobile", dataKey: 'mobile', columnOptions: {defaultWidth: 120}},
      {name: "Library", key: "library.name", dataKey: 'libraryName', columnOptions: {defaultWidth: 220}},
      {name: "Seat No", key: "seatIds", dataKey: 'seatNo', columnOptions: {defaultWidth: 130}},
      {name: "From Date", key: "fromDate", columnOptions: {defaultWidth: 110}},
      {name: "To Date", key: "toDate", columnOptions: {defaultWidth: 110}},
      {name: "Time", key: "fromTime", dataKey:"timeslot", columnOptions: {defaultWidth: 160, render: ({data}) => `${tConv24(data.fromTime)} - ${tConv24(data.toTime)}`}},
      {name: "Amount", key: "bookingTransaction.amount", dataKey: 'bookingAmount', columnOptions: {defaultWidth: 100,render: ({ value, cellProps }) => {
        return `Rs.${value}`
      }}},
      {name: 'Actions', key: "", columnOptions: { defaultWidth: 200, render: actionRender}},
    ];
    return columns;
  }, [actionRender]);

  function buildTable(data) {
    const columns = buildTableColumns(data);
    const tData = buildTableDataAndColumns(columns,data);
    setTableData({...tData});
  }

  useEffect(() => {
    if(Array.isArray(bookingsList) && bookingsList.length > 0){
      buildTable(bookingsList);
    }else{
      setTableData({});
    }
  }, [bookingsList]);
  

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
    {/* <CardContent> */}
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Live Libraries
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={3} alignItems="center"  direction="row">
          <Grid item xs={12} sm={12} md={12} xl={12} lg={12} >
            <Paper elevation={0} sx={{p:1, bgcolor: "#f3f3f3"}}>
              <Typography variant="subtitle2" gutterBottom component="div">
                Filters
              </Typography>
              <Stack direction={{sm: "column", md:"row"}} justifyContent="space-between" rowGap={1}>
                <Grid item xs={12} sm={12} md={6} xl={6} lg={6}  sx={{mt: 1}}>
                  <LibrarySelect variant="outlined" onLibrarySelected={onLibrarySelected} fullWidth={true}/>
                </Grid>
                <Grid item xs={12} sm={12} md={6} xl={6} lg={6} textAlign={{xs: 'left', sm:"right"}} sx={{mt: 1}}>
                  <ButtonGroup variant="outlined" disableElevation sx={{ display:{xs: 'none', sm: 'block'} }}>
                    {statusList.map(({id, name, color='info'}) => (<Button sx={{textTransform: 'none'}} color={color} variant={filterStatus === id ? "contained" : 'outlined'} key={id} onClick={() => statusChanged(id)}>{name}</Button>))}
                  </ButtonGroup>
                  <FormControl size="small" fullWidth sx={{ display:{xs: 'block', sm: 'none'}}}>
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      fullWidth
                      labelId="status-select-label"
                      id="status-select"
                      value={filterStatus}
                      label="Status"
                      onChange={(e) => statusChanged(e.target.value)}
                    >
                      {statusList.map(({id, name, color}) => <MenuItem  key={id} value={id}>{name}</MenuItem>)}
                      
                    </Select>
                  </FormControl>
                </Grid>
              </Stack>
            </Paper>
          </Grid>
          {tableData ? (
            <Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
              <ReactDataTableBuilder minHeight={500} tableData={tableData} /> 
            </Grid>
          ): null}
        </Grid>
      </Box>
      <Fab
        color="primary"
        variant="extended"
        onClick={onAddBookingClick}
        aria-label="Add New Booking"
        sx={{ position: 'absolute', bottom: 75, right: 75 }}
      >
        <AddIcon /> Add
      </Fab>
      <Modal maxWidth={"md"}  open={openModal} onModalClose={onModalClose} title={bookingDetails ? 'Booking Details' : 'Add Booking'}>
        <AddBooking bookingDetails={bookingDetails} onBookingAdded={onBookingAdded} />
      </Modal>
      <ScrollDialog open={moreActions.action === 'cancelBooking'} title="Cancel Booking" onModalClose={closeCancelBookingModal}>
        <CancelBooking onBookingCancelled={onBookingCancelled}  bookingId={moreActions?.data?.bookingId}/>
      </ScrollDialog>
      <Modal maxWidth={"md"}  open={moreActions.action === 'assignSeat' || moreActions.action === 'changeSeat'} title={moreActions.action === 'assignSeat' ? "Assign Seat": "Change Seat"} onModalClose={closeAllModals} >
        <SeatLayout availableSeats={availableSeats} libData={moreActions.data} onSeatAssigned={onSeatAssigned} />
      </Modal>
    </Paper>
  );
}
 
export default React.memo(BookingsPage);
